/**
 * PrivacyPolicyPage — public-facing NIPOST privacy notice.
 *
 * Pure static content ported from `screens/src/pages/legal/PrivacyPolicy.jsx`.
 * No API calls. Mounted at `/legal/privacy` in App.tsx — outside the
 * authenticated portal so anyone can read it before they register.
 */
import type { JSX } from 'react';

import { LegalLayout, type LegalSection } from '../../layouts/LegalLayout';

const SECTIONS: readonly LegalSection[] = [
  { id: 'introduction',    label: 'Introduction' },
  { id: 'controller',      label: 'Who we are' },
  { id: 'scope',           label: 'Scope of this notice' },
  { id: 'data-we-collect', label: 'Personal data we collect' },
  { id: 'how-we-use',      label: 'How we use your data' },
  { id: 'lawful-basis',    label: 'Our lawful basis' },
  { id: 'sharing',         label: 'Who we share data with' },
  { id: 'transfers',       label: 'International transfers' },
  { id: 'retention',       label: 'How long we keep data' },
  { id: 'security',        label: 'How we protect your data' },
  { id: 'rights',          label: 'Your rights' },
  { id: 'biometrics',      label: 'Biometrics & sensitive data' },
  { id: 'children',        label: "Children's data" },
  { id: 'cookies',         label: 'Cookies & tracking' },
  { id: 'automated',       label: 'Automated decisions' },
  { id: 'changes',         label: 'Changes to this notice' },
  { id: 'complaints',      label: 'Complaints' },
  { id: 'contact',         label: 'Contact us' },
];

export function PrivacyPolicyPage(): JSX.Element {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How the Nigerian Postal Service (NIPOST) collects, uses, shares and protects your personal data when you use our services, including the Unified Postal Management Platform (UPMP) and NIPOST Pay."
      effective="27 April 2026"
      lastUpdated="27 April 2026"
      sections={SECTIONS}
      sibling={{ to: '/legal/terms', label: 'Read the Terms & Conditions' }}
    >
      <div className="callout">
        <strong>Plain-language summary.</strong> We collect what we need to deliver mail, run NIPOST
        Pay, verify identities for KYC, and operate the platform safely. We never sell your data.
        Your data is stored in Nigeria. You have a clear set of rights — including access,
        correction, erasure, portability and the right to lodge a complaint with the Nigeria Data
        Protection Commission. The detail below explains exactly what we do, why, and for how long.
      </div>

      <h2 id="introduction">1. Introduction</h2>
      <p>
        The Nigerian Postal Service ("NIPOST", "we", "us", "our") respects your privacy and is
        committed to protecting the personal data you entrust to us. This Privacy Policy explains
        the personal data we collect about you, how we use it, the legal bases on which we rely,
        who we share it with, how long we keep it, how we keep it safe, and the rights you have
        over it.
      </p>
      <p>
        This notice is issued in accordance with the{' '}
        <strong>Nigeria Data Protection Act 2023 (NDPA)</strong>, the{' '}
        <strong>Nigeria Data Protection Regulation 2019 (NDPR)</strong>, and any binding
        regulations issued by the <strong>Nigeria Data Protection Commission (NDPC)</strong>.
        Where any of our financial services are concerned, it is also informed by the
        data-handling rules issued by the <strong>Central Bank of Nigeria (CBN)</strong>.
      </p>

      <h2 id="controller">2. Who we are</h2>
      <p>
        NIPOST is the data controller of the personal data described in this policy. Our
        registered identity is:
      </p>
      <ul>
        <li><strong>Controller:</strong> Nigerian Postal Service (NIPOST)</li>
        <li><strong>Registered address:</strong> NIPOST Headquarters, Garki Area 1, Abuja, FCT, Nigeria</li>
        <li><strong>Data Protection Officer:</strong> <a href="mailto:dpo@nipost.gov.ng">dpo@nipost.gov.ng</a></li>
        <li><strong>NDPC Data Controller registration:</strong> active and renewed annually</li>
      </ul>
      <p>
        Where you transact through a NIPOST franchise (a Tier 1 Full Post Office, Tier 2 Postal
        Agent or Tier 3 Last-Mile Point), the franchise operator handles your data{' '}
        <em>on behalf of NIPOST</em> as a data processor under a written agreement that requires
        them to follow this policy.
      </p>

      <h2 id="scope">3. Scope of this notice</h2>
      <p>This policy applies to personal data we collect when you:</p>
      <ul>
        <li>visit or browse the NIPOST website or any NIPOST customer-facing application;</li>
        <li>send or receive a parcel through NIPOST or a NIPOST franchise;</li>
        <li>open, fund, hold or use a NIPOST Pay wallet;</li>
        <li>complete identity verification (KYC) at any NIPOST outlet;</li>
        <li>access a government service made available through NIPOST (e.g. NIN, passport, tax payment, driver's licence);</li>
        <li>contact our customer service or service desk; or</li>
        <li>otherwise interact with NIPOST in a personal capacity.</li>
      </ul>
      <p>
        It does <em>not</em> cover the platforms of third parties (for example, an issuing
        government agency such as NIMC, or a payment switch such as NIBSS) when you transact
        directly with them — those entities have their own privacy notices.
      </p>

      <h2 id="data-we-collect">4. Personal data we collect</h2>
      <p>Depending on which services you use, we may collect the following categories of personal data.</p>

      <h3>4.1 Identity & contact</h3>
      <ul>
        <li>full name, gender, date of birth, photograph;</li>
        <li>residential and postal address, town/LGA, state;</li>
        <li>mobile telephone number and email address;</li>
        <li>National Identification Number (NIN) and Bank Verification Number (BVN), where required by law;</li>
        <li>scanned or photographed copies of government-issued identity documents.</li>
      </ul>

      <h3>4.2 Account & transaction</h3>
      <ul>
        <li>account credentials (username, hashed password, multi-factor secrets);</li>
        <li>NIPOST Pay wallet balance, top-ups, transfers, bill payments and merchant transactions;</li>
        <li>parcel records: tracking numbers, sender/recipient details, weight, declared value, status events;</li>
        <li>government service applications you submit through us (e.g. NIN slip request, passport intake);</li>
        <li>complaint, ticket and customer-service conversation history.</li>
      </ul>

      <h3>4.3 Identity verification & biometric</h3>
      <ul>
        <li>captured face image and liveness data;</li>
        <li>fingerprint templates (where you choose fingerprint as your biometric);</li>
        <li>verification scores returned by NIMC, NIBSS or other accredited identity sources;</li>
        <li>watchlist / sanctions / PEP screening outcomes.</li>
      </ul>

      <h3>4.4 Device, technical & usage</h3>
      <ul>
        <li>IP address, device type, operating system, browser, device fingerprint;</li>
        <li>application version, crash and diagnostic logs;</li>
        <li>approximate location (city / LGA) derived from IP, and precise GPS only when you give the mobile app permission to use it;</li>
        <li>login times, pages visited, transactions performed.</li>
      </ul>

      <h3>4.5 Sources outside this policy</h3>
      <p>Some of your data reaches us from third parties:</p>
      <ul>
        <li>verification responses from <strong>NIMC</strong> (NIN), <strong>NIBSS</strong> (BVN), <strong>FRSC</strong> (driver's licence) and similar agencies;</li>
        <li>card-network and switch responses for payments;</li>
        <li>watchlist matches from sanctions and PEP databases used for AML screening.</li>
      </ul>

      <h2 id="how-we-use">5. How we use your data</h2>
      <p>We use your personal data to:</p>
      <ul>
        <li><strong>deliver the service</strong> you asked for — register and dispatch parcels; settle payments; verify your identity to the legally required tier; process government service applications;</li>
        <li><strong>operate your account</strong> — authenticate you, secure your wallet, send transaction receipts and status updates;</li>
        <li><strong>communicate with you</strong> — respond to enquiries, complaints and service-desk tickets, send service-related notifications, and (with your consent) marketing about NIPOST products;</li>
        <li><strong>comply with the law</strong> — meet our obligations under the Nigerian Postal Service Act, the Banks and Other Financial Institutions Act (BOFIA), CBN AML/CFT rules, the NDPA/NDPR, and the duties NIPOST has as the regulator of private courier and logistics operators;</li>
        <li><strong>prevent fraud & abuse</strong> — screen transactions, detect anomalies, investigate suspected fraud, money laundering or terrorism financing;</li>
        <li><strong>improve our services</strong> — analyse aggregated usage, run service-quality reviews, and plan capacity in our network;</li>
        <li><strong>defend our legitimate interests</strong> — pursue claims, protect property, and respond to lawful requests from regulators or courts.</li>
      </ul>

      <h2 id="lawful-basis">6. Our lawful basis for processing</h2>
      <p>We rely on one or more of the following lawful bases under section 25 of the NDPA:</p>
      <table>
        <thead>
          <tr><th>Lawful basis</th><th>When we rely on it</th></tr>
        </thead>
        <tbody>
          <tr><td>Performance of a contract</td><td>Most service-delivery activities — parcel handling, wallet operations, government service submissions you initiate.</td></tr>
          <tr><td>Legal obligation</td><td>Identity verification (NDPA, AML/CFT), tax reporting, audit retention, regulatory reporting (CBN, NFIU), records requested by lawful authorities.</td></tr>
          <tr><td>Public interest task</td><td>Operating the national postal network and regulating private courier/logistics operators on behalf of the Federal Government of Nigeria.</td></tr>
          <tr><td>Consent</td><td>Optional features such as marketing, biometric capture, location sharing, and special-category data where consent is required.</td></tr>
          <tr><td>Legitimate interests</td><td>Fraud prevention, network security, service improvement, and defending or asserting legal claims — only where your rights and freedoms are not overridden.</td></tr>
        </tbody>
      </table>

      <h2 id="sharing">7. Who we share your data with</h2>
      <p>We disclose personal data only when there is a clear basis to do so. Recipients fall into the following categories:</p>
      <ul>
        <li><strong>NIPOST franchise operators</strong> (Tier 1, 2 and 3) handling your transaction at the counter, under a binding processor agreement;</li>
        <li><strong>Government agencies</strong> we serve as an agent or to whom we are required to report — including NIMC, NIBSS, FRSC, FIRS, INEC, NIS, NHIS, the CBN, the NFIU, the NDPC and the courts;</li>
        <li><strong>Payment service providers and switches</strong> that settle wallet top-ups, transfers and bill payments;</li>
        <li><strong>Telecommunication providers</strong> we use to send you SMS messages and push notifications;</li>
        <li><strong>Trusted technology partners</strong> who run hosting, analytics, anti-fraud and security tooling for us — including INITS Limited, our platform partner, who operate the UPMP under a written processor agreement;</li>
        <li><strong>Professional advisers</strong> (auditors, lawyers, insurers) when needed to defend or assert our legal interests;</li>
        <li><strong>Successors</strong> in the event of a corporate restructure, where the law permits.</li>
      </ul>
      <p>We <strong>do not sell</strong> your personal data, and we never share it for third-party advertising.</p>

      <h2 id="transfers">8. International transfers and data residency</h2>
      <p>
        Personal data we collect is stored and processed within Nigeria. We host the UPMP and the
        NIPOST Pay infrastructure inside Nigerian data-centre facilities and apply data-residency
        controls to keep production data within Nigerian borders.
      </p>
      <p>
        On rare and narrowly defined occasions — for example, a cross-border parcel delivery to a
        foreign postal administration in line with the Universal Postal Union, or an inter-bank
        settlement involving a foreign correspondent — limited identifying information may be
        transferred outside Nigeria. When this happens, we rely on an adequacy decision, your
        explicit consent, or another lawful transfer mechanism recognised by the NDPA, and we
        share only the minimum data required.
      </p>

      <h2 id="retention">9. How long we keep your data</h2>
      <p>We retain personal data only for as long as we have a legal or operational need:</p>
      <table>
        <thead>
          <tr><th>Category</th><th>Retention period</th></tr>
        </thead>
        <tbody>
          <tr><td>Account profile (active customer)</td><td>For the duration of the account, plus 3 years after closure.</td></tr>
          <tr><td>Transaction & financial records (CBN)</td><td>10 years from the date of transaction.</td></tr>
          <tr><td>KYC records & identity evidence</td><td>5 years after end of customer relationship (CBN AML/CFT).</td></tr>
          <tr><td>Mail item & POD records</td><td>3 years from the date of delivery or final disposition.</td></tr>
          <tr><td>Audit & security logs</td><td>7 years from the date of the event (financial-impact events) or 3 years (general).</td></tr>
          <tr><td>Marketing preferences</td><td>Until you withdraw consent.</td></tr>
          <tr><td>Customer-service call & chat logs</td><td>2 years from the closure of the case.</td></tr>
          <tr><td>Cookies and analytics</td><td>Up to 13 months unless you delete them sooner.</td></tr>
        </tbody>
      </table>
      <p>
        After the retention period ends, we delete or irreversibly anonymise the data. Where
        erasure is technically infeasible (for example, immutable audit chains), we isolate the
        data and ensure it cannot be used for any new purpose.
      </p>

      <h2 id="security">10. How we protect your data</h2>
      <p>We apply layered technical and organisational measures, including:</p>
      <ul>
        <li><strong>Encryption</strong>: AES-256 at rest, TLS 1.3 in transit. Sensitive identifiers (NIN, BVN) are stored in tokenised form.</li>
        <li><strong>Strict access control</strong>: role-based and field-level permissions with operator scoping; multi-factor authentication for all staff with elevated rights.</li>
        <li><strong>Tamper-evident audit trails</strong> for all financial and security-impact actions.</li>
        <li><strong>Regular security testing</strong>: SAST, DAST, dependency scanning and at least annual independent penetration testing.</li>
        <li><strong>Hosting in Nigeria</strong> with mTLS between services and a hardened service mesh.</li>
        <li><strong>Staff training & confidentiality</strong> obligations for everyone handling personal data.</li>
      </ul>

      <h2 id="rights">11. Your rights</h2>
      <p>Subject to the conditions in the NDPA, you have the following rights:</p>
      <ul>
        <li><strong>Right to be informed</strong> — through this notice and any specific notices we issue.</li>
        <li><strong>Right of access</strong> — to a copy of the personal data we hold about you.</li>
        <li><strong>Right to rectification</strong> — to correct inaccurate or incomplete data.</li>
        <li><strong>Right to erasure</strong> — to ask us to delete your data, subject to overriding legal-retention obligations.</li>
        <li><strong>Right to restriction</strong> — to limit how we use your data while a dispute is being resolved.</li>
        <li><strong>Right to data portability</strong> — to receive your data in a structured, machine-readable format and have it transmitted to another controller where technically feasible.</li>
        <li><strong>Right to object</strong> — to processing based on legitimate interests or for direct marketing.</li>
        <li><strong>Right to withdraw consent</strong> — at any time, where consent is the lawful basis. Withdrawal does not affect prior lawful processing.</li>
        <li><strong>Right not to be subject</strong> to a solely automated decision producing legal effects, where applicable.</li>
        <li><strong>Right to lodge a complaint</strong> with the Nigeria Data Protection Commission (NDPC).</li>
      </ul>
      <p>
        To exercise any right, write to{' '}
        <a href="mailto:dpo@nipost.gov.ng">dpo@nipost.gov.ng</a> or visit any NIPOST outlet with
        valid identification. We will respond within <strong>30 days</strong> of receiving a
        verifiable request, and we will not charge you a fee for routine requests.
      </p>

      <h2 id="biometrics">12. Biometrics and sensitive data</h2>
      <p>
        Biometric data (face image, fingerprint template) is treated as{' '}
        <strong>special-category personal data</strong>. We capture it only for identity
        verification and only with your explicit consent. We store the cryptographic{' '}
        <em>reference</em> required to perform a match — not the raw biometric image — and we
        never share biometric data for any purpose other than verification with the relevant
        accredited identity authority. You may withdraw consent and request deletion of your
        biometric reference at any time, subject to any minimum statutory KYC retention.
      </p>

      <h2 id="children">13. Children's data</h2>
      <p>
        Our services are intended for individuals aged 18 years and older. We do not knowingly
        process personal data of children except as part of an application made by a parent or
        legal guardian (for example, a passport or NIN application for a minor). Where we process
        such data, we apply additional safeguards and rely on the consent of the parent or
        guardian.
      </p>

      <h2 id="cookies">14. Cookies and similar technologies</h2>
      <p>
        We use a small number of cookies and similar technologies to keep you signed in, remember
        your preferences, secure the service, and measure aggregate usage. You can manage cookies
        through your browser settings; blocking strictly-necessary cookies may break parts of the
        service. We do not use cookies for cross-site advertising.
      </p>

      <h2 id="automated">15. Automated decisions and profiling</h2>
      <p>
        Some routine decisions are automated — for example, transaction-monitoring rules that
        flag a wallet operation for review, or biometric match scoring during KYC. Where an
        automated decision produces a significant effect on you (for example, a transaction is
        blocked), you may request human review by contacting customer support. We do not use
        automated decisions for any solely commercial purpose.
      </p>

      <h2 id="changes">16. Changes to this notice</h2>
      <p>
        We may update this notice from time to time to reflect changes in our services or in the
        law. Material changes will be communicated to you in advance through your registered
        email address, your NIPOST Pay app, or a banner on the website. The "Last updated" date
        at the top of this page tells you when the most recent revision was published. Continued
        use of our services after the effective date of an updated notice indicates your
        awareness of the changes.
      </p>

      <h2 id="complaints">17. Complaints</h2>
      <p>
        We would like the chance to resolve any concern first. Please contact our Data Protection
        Officer at <a href="mailto:dpo@nipost.gov.ng">dpo@nipost.gov.ng</a>. If you are not
        satisfied with the outcome, you have the right to lodge a complaint with the:
      </p>
      <ul>
        <li>
          <strong>Nigeria Data Protection Commission (NDPC)</strong>, No. 234 Muhammadu Buhari
          Way, Central Business District, Abuja, FCT —{' '}
          <a href="mailto:complaints@ndpc.gov.ng">complaints@ndpc.gov.ng</a>.
        </li>
      </ul>
      <p>
        Complaints relating to financial transactions may also be raised with the{' '}
        <strong>Central Bank of Nigeria Consumer Protection Department</strong> in line with
        CBN's Consumer Protection Framework.
      </p>

      <h2 id="contact">18. Contact us</h2>
      <p>
        For any privacy question, request, or concern, please reach our Data Protection Officer:
      </p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:dpo@nipost.gov.ng">dpo@nipost.gov.ng</a></li>
        <li><strong>Customer support:</strong> 0800-NIPOST (0800-647-678)</li>
        <li><strong>Postal address:</strong> Office of the Data Protection Officer, NIPOST Headquarters, Garki Area 1, Abuja, FCT</li>
      </ul>

      <div className="callout">
        <strong>Document status.</strong> This notice has been prepared as part of the UPMP
        roll-out and will be reviewed and signed off by NIPOST's legal counsel and the
        NDPC-accredited Data Protection Compliance Organisation (DPCO) before publication.
      </div>
    </LegalLayout>
  );
}
